import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Upload, Trash2, Building2 } from "lucide-react";
import CompanyCatalogs from "@/components/CompanyCatalogs";
import TeamMembers from "@/components/TeamMembers";

export default function CompanySettings() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    name: "",
    company_name: "",
    company_phone: "",
    company_email: "",
    company_website: "",
    company_logo: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        company_name: user.company_name || "",
        company_phone: user.company_phone || "",
        company_email: user.company_email || "",
        company_website: user.company_website || "",
        company_logo: user.company_logo || "",
      });
    }
  }, [user]);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 1024 * 1024 * 2) {
      toast.error("Logo on liian suuri (max 2MB)");
      return;
    }
    const compressed = await compressImage(file);
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, company_logo: reader.result }));
    reader.readAsDataURL(compressed);
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/auth/me", form);
      setUser(data);
      toast.success("Tiedot tallennettu");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tallennus epäonnistui");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-6 md:py-8">
        <button onClick={() => navigate("/app")} className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-4" data-testid="settings-back">
          <ArrowLeft className="h-4 w-4" /> Takaisin
        </button>

        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-emerald-600 font-semibold">Asetukset</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-slate-900 mt-1">Yritys ja brändi</h1>
          <p className="mt-2 text-slate-600">Nämä tiedot näkyvät asiakkaalle lähetettävissä raporteissa ja tiedotteissa.</p>
        </div>

        <div className="card-soft p-5 md:p-8 space-y-6">
          {/* Logo */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Yrityksen logo</label>
            <div className="flex items-center gap-4">
              {form.company_logo ? (
                <img src={form.company_logo} alt="Logo" className="h-20 w-20 rounded-2xl object-cover border border-slate-100 bg-slate-50" data-testid="settings-logo-preview" />
              ) : (
                <div className="h-20 w-20 rounded-2xl bg-slate-50 border border-dashed border-slate-300 flex items-center justify-center text-slate-400">
                  <Building2 className="h-8 w-8" />
                </div>
              )}
              <div className="flex-1">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  data-testid="settings-logo-input"
                />
                <button onClick={() => fileRef.current?.click()} className="btn-ghost text-sm" data-testid="settings-logo-upload">
                  <Upload className="h-4 w-4" /> {form.company_logo ? "Vaihda logo" : "Lataa logo"}
                </button>
                {form.company_logo && (
                  <button
                    onClick={() => setForm((f) => ({ ...f, company_logo: "" }))}
                    className="btn-ghost text-sm ml-2 text-red-600"
                    data-testid="settings-logo-remove"
                  >
                    <Trash2 className="h-4 w-4" /> Poista
                  </button>
                )}
                <p className="text-xs text-slate-500 mt-2">Suositus: neliömäinen PNG, max 2MB. Näkyy raportin ylälaidassa.</p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Nimesi</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-base" data-testid="settings-name" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Yrityksen nimi</label>
              <input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="input-base" placeholder="esim. Rakennuspalvelu Veraso Oy" data-testid="settings-company" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Puhelin</label>
              <input value={form.company_phone} onChange={(e) => setForm({ ...form, company_phone: e.target.value })} className="input-base" data-testid="settings-phone" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Sähköposti</label>
              <input value={form.company_email} onChange={(e) => setForm({ ...form, company_email: e.target.value })} className="input-base" data-testid="settings-email" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Verkkosivu</label>
              <input value={form.company_website} onChange={(e) => setForm({ ...form, company_website: e.target.value })} className="input-base" placeholder="https://" data-testid="settings-website" />
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <button onClick={save} disabled={busy} className="btn-primary disabled:opacity-50" data-testid="settings-save">
              {busy ? "Tallennetaan..." : "Tallenna"}
            </button>
          </div>
        </div>

        {/* Company-wide catalogs: checklists + materials */}
        <div className="mt-8">
          <CompanyCatalogs />
        </div>

        {/* Tiimi: kutsu ja hallinnoi kenttäkäyttäjiä */}
        <div className="mt-8">
          <TeamMembers />
        </div>
      </div>
    </div>
  );
}

async function compressImage(file, maxDim = 512, quality = 0.9) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          if (width > height) { height = Math.round((height / width) * maxDim); width = maxDim; }
          else { width = Math.round((width / height) * maxDim); height = maxDim; }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => resolve(new File([blob], file.name, { type: "image/png" })), "image/png", quality);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  });
}
